import React, { useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../../../firebase';
import { 
  Wallet as WalletIcon, 
  ArrowUpRight, 
  ArrowDownLeft, 
  RefreshCw, 
  Copy, 
  CheckCircle2, 
  Eye, 
  Plus,
  AlertCircle,
  Lock,
  Unlock
} from 'lucide-react';
import "./Wallet.css";

import { API_BASE } from "../../../config/api";

const Wallet = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Dynamic Financial State Nodes loaded directly from your MongoDB via backend API
  const [financials, setFinancials] = useState({
    totalPortfolio: 0.00,
    netProfitLoss: 0.00,
    availableLiquidity: 0.00,
    lockedInvestment: 0.00, // ✅ NEW: Track locked principal
    totalDeposited: 0.00,
    totalWithdrawn: 0.00
  });

  // Transaction state tracks empty initially until deposit hooks record movement data
  const [recentTransactions, setRecentTransactions] = useState([]);

  // ==========================================================================
  // HELPER: Get JWT token from localStorage
  // ==========================================================================
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // ==========================================================================
  // FETCH WALLET DATA WITH JWT AUTHENTICATION
  // ==========================================================================
  const fetchWalletData = useCallback(async () => {
    try {
      setError(null);
      
      const headers = getAuthHeaders();
      
      console.log('💰 Fetching wallet data with JWT token...');
      
      // ✅ Use JWT token instead of email in URL
      const response = await axios.get(`${API_BASE}/api/identity/dashboard-metrics`, { headers });
      
      if (response.data.success && response.data.balances) {
        const balances = response.data.balances;
        
        setFinancials({
          totalPortfolio: balances.totalPortfolio || 0,
          netProfitLoss: balances.netProfitLoss || 0,
          availableLiquidity: balances.availableLiquidity || 0,
          lockedInvestment: balances.lockedInvestment || 0, // ✅ NEW
          totalDeposited: balances.totalDeposited || 0,
          totalWithdrawn: balances.totalWithdrawn || 0
        });
        
        console.log('✅ Wallet data loaded:', balances);
        
        // Dynamic mockup of transactions array conditional on having a balance
        if (balances.availableLiquidity > 0 || balances.lockedInvestment > 0) {
          setRecentTransactions([
            { 
              id: 'TX-9021', 
              type: 'Investment', 
              asset: 'USDT', 
              amount: `+${(balances.lockedInvestment || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 
              date: 'Active Investment', 
              status: 'Locked', 
              isCredit: false 
            }
          ]);
          
          if (balances.availableLiquidity > 0) {
            setRecentTransactions(prev => [
              { 
                id: 'TX-9022', 
                type: 'Profit', 
                asset: 'USDT', 
                amount: `+${balances.availableLiquidity.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, 
                date: 'Available', 
                status: 'Withdrawable', 
                isCredit: true 
              },
              ...prev
            ]);
          }
        } else {
          setRecentTransactions([]);
        }
      }
    } catch (err) {
      console.error("❌ Backend wallet communication error:", err);
      
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.clear();
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else {
        setError(err.response?.data?.message || 'Failed to load wallet data');
      }
    }
  }, []);

  // ==========================================================================
  // REAL-TIME AUTHENTICATION TRACKER & LIQUIDITY FETCH PIPELINE
  // ==========================================================================
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser({
          uid: user.uid,
          email: user.email
        });

        await fetchWalletData();
        setLoadingMetrics(false);
      } else {
        setCurrentUser(null);
        setLoadingMetrics(false);
      }
    });
    return () => unsubscribeAuth();
  }, [fetchWalletData]);

  // ✅ Auto-refresh every 30 seconds
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(() => {
      fetchWalletData();
    }, 30000);
    return () => clearInterval(interval);
  }, [currentUser, fetchWalletData]);

  // ✅ Manual refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWalletData();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const copyAddress = () => {
    navigator.clipboard.writeText('0x71C2293297489394A3B8f3ba2fC71CB29');
    alert('Wallet destination key copied to clipboard.');
  };

  // Dynamically map balances based on true backend liquidity context strings
  const liquidity = financials.availableLiquidity;
  const locked = financials.lockedInvestment;
  
  const walletBalances = [
    { 
      id: 1, 
      currency: 'USDT', 
      name: 'Tether USD Wallet', 
      amount: liquidity > 0 ? liquidity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00', 
      fiatValue: `$${liquidity > 0 ? liquidity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}`, 
      network: 'TRC-20 / ERC-20 Node',
      type: 'available'
    },
    { 
      id: 2, 
      currency: 'USD', 
      name: 'Fiat Capital Balance', 
      amount: '0.00', 
      fiatValue: '$0.00', 
      network: 'FedWire / ACH',
      type: 'available'
    }
  ];

  if (loadingMetrics) {
    return (
      <div className="erax-dashboard-wrapper flex items-center justify-center" style={{ minHeight: '80vh', color: '#94A3B8' }}>
        <div className="text-center">
          <p className="font-mono text-sm animate-pulse text-[#f3ba2f]">SYNCHRONIZING LIQUIDITY WALLET VAULT...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="erax-dashboard-wrapper flex items-center justify-center" style={{ minHeight: '80vh', color: '#f87171' }}>
        <div className="text-center">
          <AlertCircle size={48} style={{ marginBottom: '16px' }} />
          <p style={{ marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={handleRefresh}
            style={{ 
              padding: '10px 20px', 
              background: '#1e293b', 
              border: 'none', 
              borderRadius: '8px', 
              color: '#fff', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
          >
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="erax-dashboard-wrapper">
      
      {/* 1. PRIMARY METRIC STRIP */}
      <div className="erax-stats-strip wallet-hero-grid">
        <div className="erax-stat-node wallet-main-card">
          <div className="wallet-card-header">
            <span className="erax-node-label"><WalletIcon size={14} /> Total Portfolio Value</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span className="secure-badge"><Eye size={12} /> Verified Asset Balance</span>
              <button 
                onClick={handleRefresh}
                disabled={refreshing}
                style={{ 
                  background: 'transparent', 
                  border: '1px solid rgba(255,255,255,0.1)', 
                  borderRadius: '6px', 
                  padding: '4px 8px', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#94a3b8',
                  fontSize: '12px'
                }}
              >
                <RefreshCw size={12} className={refreshing ? 'spin' : ''} />
                {refreshing ? 'Syncing...' : 'Refresh'}
              </button>
            </div>
          </div>
          <div className="wallet-balance-hero">
            <h2>${(liquidity + locked).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h2>
            <span className="currency-tag">Total Portfolio</span>
          </div>
          
          {/* ✅ NEW: Available vs Locked Breakdown */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '16px', 
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(255,255,255,0.05)'
          }}>
            {/* Available Balance Card */}
            <div style={{ 
              background: 'rgba(16, 185, 129, 0.1)', 
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px', 
              padding: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Unlock size={16} style={{ color: '#10b981' }} />
                <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '600' }}>AVAILABLE</span>
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981', marginBottom: '4px' }}>
                ${liquidity.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                Profit • Withdrawable
              </div>
            </div>

            {/* Locked Investment Card */}
            <div style={{ 
              background: 'rgba(245, 158, 11, 0.1)', 
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px', 
              padding: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Lock size={16} style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600' }}>LOCKED</span>
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b', marginBottom: '4px' }}>
                ${locked.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                Principal • Auto-Renewing
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '12px', 
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(255,255,255,0.05)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Total Deposited</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#22c55e' }}>
                ${financials.totalDeposited.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Total Withdrawn</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#ef4444' }}>
                ${financials.totalWithdrawn.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>Net Profit/Loss</div>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: financials.netProfitLoss >= 0 ? '#22c55e' : '#ef4444' 
              }}>
                {financials.netProfitLoss >= 0 ? '+' : ''}${financials.netProfitLoss.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. DUAL INDUSTRIAL WORKSPACE MATRIX */}
      <div className="erax-split-workspace">
        
        {/* LEFT COLUMN: CURRENCY NODES & TRANSFERS */}
        <div className="erax-workspace-panel main-ledger-panel">
          <div className="panel-header-action">
            <div>
              <h3 className="panel-title">Available Balances</h3>
              <p className="panel-subtitle">Liquid funds ready for withdrawal</p>
            </div>
            <button className="live-pulse-container btn-add-wallet" style={{ border: '1px solid rgba(255,255,255,0.05)', background: 'transparent', cursor: 'pointer' }}>
              <Plus size={14} /> Link Account
            </button>
          </div>

          <div className="holdings-table-container">
            {walletBalances.map((balance) => (
              <div key={balance.id} className="holding-row-card wallet-balance-row">
                <div className={`holding-icon-box ${balance.id === 2 ? 'bonds-tint' : 'stocks-tint'}`} style={{ backgroundColor: liquidity > 0 || balance.id !== 1 ? '' : 'rgba(255,255,255,0.02)' }}>
                  <span className="currency-symbol-txt" style={{ color: liquidity > 0 || balance.id !== 1 ? '#f3ba2f' : '#475569' }}>{balance.currency}</span>
                </div>

                <div className="holding-identity-cell">
                  <h4>{balance.name}</h4>
                  <span>Network: {balance.network}</span>
                </div>

                <div className="holding-data-cell text-right">
                  <span className="cell-top market-value" style={{ color: liquidity > 0 && balance.id === 1 ? '#ffffff' : '#475569' }}>
                    {balance.amount} {balance.currency}
                  </span>
                  <span className="cell-bottom wallet-fiat-subtitle">{balance.fiatValue}</span>
                </div>
              </div>
            ))}

            {/* ✅ NEW: Locked Investment Display */}
            {locked > 0 && (
              <div className="holding-row-card wallet-balance-row" style={{ 
                background: 'rgba(245, 158, 11, 0.05)',
                border: '1px solid rgba(245, 158, 11, 0.2)'
              }}>
                <div className="holding-icon-box" style={{ backgroundColor: 'rgba(245, 158, 11, 0.2)' }}>
                  <Lock size={16} style={{ color: '#f59e0b' }} />
                </div>

                <div className="holding-identity-cell">
                  <h4 style={{ color: '#f59e0b' }}>Locked Investment Principal</h4>
                  <span>Auto-renewing every 30 days</span>
                </div>

                <div className="holding-data-cell text-right">
                  <span className="cell-top market-value" style={{ color: '#f59e0b' }}>
                    {locked.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                  </span>
                  <span className="cell-bottom wallet-fiat-subtitle" style={{ color: '#f59e0b' }}>
                    🔒 Cannot Withdraw
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: ACTION CONTROLLERS & INTERFACE */}
        <div className="erax-workspace-panel analytics-side-panel">
          
          {/* QUICK TRANSFERS PANEL */}
          <div className="inner-analytics-card premium-action-card wallet-action-card">
            <h4 className="card-inner-title"><RefreshCw size={16} /> Quick Funds Transfer</h4>
            <p className="terminal-desc">Move capital instantly between your cash balances and investment accounts.</p>
            
            <div className="wallet-action-row-buttons">
              <button className="wallet-action-btn"><ArrowDownLeft size={16} /> Deposit</button>
              <button className="wallet-action-btn" disabled={liquidity === 0} style={{ opacity: liquidity > 0 ? 1 : 0.4, cursor: liquidity > 0 ? 'pointer' : 'not-allowed' }}><ArrowUpRight size={16} /> Withdraw</button>
              <button className="wallet-action-btn" disabled={liquidity === 0} style={{ opacity: liquidity > 0 ? 1 : 0.4, cursor: liquidity > 0 ? 'pointer' : 'not-allowed' }}><RefreshCw size={14} /> Swap</button>
            </div>
          </div>

          {/* ✅ NEW: Investment Info Card */}
          {locked > 0 && (
            <div className="inner-analytics-card" style={{ 
              background: 'rgba(245, 158, 11, 0.05)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              marginTop: '16px'
            }}>
              <h4 className="card-inner-title" style={{ color: '#f59e0b' }}>
                <Lock size={16} /> Perpetual Investment
              </h4>
              <p className="terminal-desc" style={{ color: '#94a3b8', fontSize: '13px' }}>
                Your locked principal of <strong style={{ color: '#f59e0b' }}>${locked.toFixed(2)}</strong> is continuously earning 100% ROI every 30 days. 
                Profits are automatically added to your available balance.
              </p>
              <div style={{ 
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#f59e0b'
              }}>
                💡 You can withdraw your profits anytime, but the principal remains locked to continue earning.
              </div>
            </div>
          )}

          {/* FUNDING DESTINATION CARD */}
          <div className="inner-analytics-card wallet-address-card">
            <div className="address-card-header">
              <h5>Fast Funding Key (USDT)</h5>
              <span className="network-pill">ERC-20 / TRC-20</span>
            </div>
            <div className="address-copy-box" onClick={copyAddress} style={{ cursor: 'pointer' }}>
              <code>0x71C22932974893...B29</code>
              <Copy size={14} className="text-gold" />
            </div>
          </div>

        </div>

      </div>

      {/* 3. RECENT TRANSACTION STATEMENT LEDGER */}
      <div className="erax-workspace-panel full-width-ledger">
        <div className="panel-header-action">
          <div>
            <h3 className="panel-title">Recent Ledger Logs</h3>
            <p className="panel-subtitle">A live record of account fund settlement status</p>
          </div>
        </div>

        <div className="holdings-table-container">
          {recentTransactions.length > 0 ? (
            recentTransactions.map((tx) => (
              <div key={tx.id} className="holding-row-card tx-ledger-row">
                <div className={`holding-icon-box ${tx.isCredit ? 'tx-credit-tint' : 'tx-debit-tint'}`}>
                  {tx.isCredit ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                </div>

                <div className="holding-identity-cell">
                  <h4>{tx.type} Asset Order</h4>
                  <span>Ref: {tx.id} • {tx.date}</span>
                </div>

                <div className="holding-data-cell hide-mobile">
                  <span className="cell-top">Asset Vector</span>
                  <span className="cell-bottom text-white">{tx.asset} Ledger</span>
                </div>

                <div className="holding-data-cell text-right">
                  <span className={`cell-top market-value ${tx.isCredit ? 'text-green' : 'text-white'}`}>
                    {tx.amount}
                  </span>
                  <span className="cell-bottom tx-status-indicator">
                    <CheckCircle2 size={12} /> {tx.status}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '36px 20px', color: '#475569', fontSize: '0.85rem' }}>
              No recent asset movements registered. Deploy tracking address configurations above to activate settlement logging.
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Wallet;